import { postApiB2bApplicationByIdFormAmendMeta } from '/api/sdk.gen';
import type {
  PostApiB2bApplicationByIdFormAmendMetaData,
  PostApiB2bApplicationByIdFormAmendMetaResponse,
} from '/api/types.gen';

import { getFormState, getApplication } from '/business/shared/state/actions/application';
import { refreshSession } from '/business/shared/state/actions/auth';
import normaliseError, { toNormaliseErrorInput } from '/shared/functions/normaliseError';
import { showModal } from '/shared/state/actions/modal';
import type { AppDispatch } from '/business/shared/state/store';

type FormAmendMetaBody = NonNullable<PostApiB2bApplicationByIdFormAmendMetaData['body']>;

interface Props {
  item: {
    key: FormAmendMetaBody['metaKey'];
    data: FormAmendMetaBody['metaItem'];
  } | null;
  applicationId: number | null;
  isConfirmedByUser?: boolean;
  skipSessionRenewal?: boolean;
}

type AmendMetaResponse = PostApiB2bApplicationByIdFormAmendMetaResponse;

const setBasketMeta =
  ({ item = null, applicationId, skipSessionRenewal = false, isConfirmedByUser = false }: Props) =>
  async (dispatch: AppDispatch) => {
    if (!applicationId) {
      return;
    }
    if (!item || !item.key || !item.data) {
      return;
    }

    try {
      await dispatch({ type: 'BSK_SAVE_ATTEMPT_BEGUN' });
      if (!skipSessionRenewal) {
        await dispatch(refreshSession({ force: true }));
      }

      const headers: {
        'x-cmp-confirm'?: string;
      } = {};
      if (isConfirmedByUser) {
        headers['x-cmp-confirm'] = 'y';
      }

      const { data: res } = await postApiB2bApplicationByIdFormAmendMeta({
        path: { id: applicationId.toString() },
        body: {
          metaItem: item.data,
          metaKey: item.key,
        },
        headers,
      });

      const { revalidate = false, revalidateSpecific = [] }: Partial<AmendMetaResponse> = res ?? {};
      if (revalidate) {
        await dispatch(getFormState(applicationId));
        await dispatch(getApplication(applicationId));
        await dispatch({ type: 'BSK_SAVE_ATTEMPT_COMPLETE', partial: false });
      } else if (revalidateSpecific.length > 0) {
        await dispatch(getFormState(applicationId, [], revalidateSpecific));
        await dispatch({ type: 'BSK_SAVE_ATTEMPT_COMPLETE', partial: true });
      }
    } catch (error) {
      const { code } = normaliseError(toNormaliseErrorInput(error));
      if (code === 'ERRAPIREQUESTREQUIRESCONFIRMATION' && !isConfirmedByUser) {
        await dispatch({ type: 'BSK_SAVE_ATTEMPT_REQ_CONFIRM' });
        dispatch(
          showModal({
            name: 'REQUEST_CONFIRM_OF_SAVE',
            isBlocking: true,
            metadata: {
              skipSessionRenewal,
              item,
              applicationId,
            },
          }),
        );
        return;
      }
      await dispatch({ type: 'BSK_SAVE_ATTEMPT_REJECTED', error });
      await dispatch({ type: 'META_CHANGE_REJECTED', error });

      dispatch(
        showModal({
          name: 'UNKNOWN_ERROR',
          isBlocking: true,
          metadata: {
            message: 'There was an issue syncing your form with the server.',
            error: toNormaliseErrorInput(error),
          },
        }),
      );

      throw error;
    }
  };

export default setBasketMeta;
